import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import AdminLayout from './AdminLayout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Switch } from '../../components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { toast } from 'sonner';
import {
  Mail, Edit, Eye, Save, X, RefreshCw, Code,
  Bold, Italic, Underline, Link, Type, AlignLeft, AlignCenter,
  List, ListOrdered, Variable, Palette, Undo, Redo
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Standard email HTML wrapper — keeps all templates consistent
function buildEmailHtml({ headerTitle, bodyHtml, buttonText, buttonUrl, footerText, showLogo }) {
  return `<!DOCTYPE html>
<html>
<head>
    <meta charset="utf-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
</head>
<body style="margin: 0; padding: 0; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; background-color: #f5f5f5;">
    <div style="max-width: 600px; margin: 0 auto; padding: 30px 20px;">
        <div style="background: #ffffff; border-radius: 8px; padding: 30px; box-shadow: 0 2px 4px rgba(0,0,0,0.1);">
            <div style="text-align: center; padding: 20px; background: linear-gradient(135deg, #78BE20 0%, #5a9a18 100%); border-radius: 8px; margin-bottom: 25px;">
                ${showLogo ? `<img src="https://customer-assets.emergentagent.com/job_58aca9c2-2cb6-4ff9-a615-17272856b112/artifacts/wb3ejkz9_Logo%20-%20Transparent.png" alt="Tech Distribution" width="160" style="max-width: 160px; height: auto;" />` : ''}
                <h1 style="color: #fff; margin: ${showLogo ? '10px' : '0'} 0 0 0; font-size: 22px;">${headerTitle}</h1>
            </div>

            <div style="color: #333; font-size: 15px; line-height: 1.7;">
                ${bodyHtml}
            </div>

            ${buttonText ? `
            <div style="text-align: center; margin: 30px 0;">
                <a href="${buttonUrl || '#'}" style="display: inline-block; background: linear-gradient(135deg, #78BE20, #5a9a18); color: #fff; padding: 14px 32px; text-decoration: none; border-radius: 6px; font-weight: bold; font-size: 14px;">${buttonText}</a>
            </div>` : ''}

            <hr style="border: none; border-top: 1px solid #eee; margin: 25px 0;" />
            <p style="color: #999; font-size: 12px; text-align: center; margin: 0;">
                ${footerText || 'This is an automated email from Tech Distribution Australia.'}
            </p>
        </div>
    </div>
</body>
</html>`;
}

// Parse existing HTML template into editable sections
function parseTemplateSections(html) {
  const sections = {
    headerTitle: '',
    bodyHtml: '',
    buttonText: '',
    buttonUrl: '',
    footerText: 'This is an automated email from Tech Distribution Australia.',
    showLogo: true,
  };

  if (!html) return sections;

  // Check for logo
  sections.showLogo = html.includes('Logo') && html.includes('Transparent');

  // Extract header title — try multiple patterns
  // Pattern 1: <h1> or <h2> in a styled header area
  const headerPatterns = [
    /<h1[^>]*>([\s\S]*?)<\/h1>/i,
    /<h2[^>]*color:\s*#fff[^>]*>([\s\S]*?)<\/h2>/i,
    /<h2[^>]*color:\s*#ffffff[^>]*>([\s\S]*?)<\/h2>/i,
  ];
  for (const pat of headerPatterns) {
    const m = html.match(pat);
    if (m) { sections.headerTitle = m[1].replace(/<[^>]*>/g, '').trim(); break; }
  }

  // Extract button — find the main CTA link with background styling
  const btnPatterns = [
    /<a[^>]*href="([^"]*)"[^>]*background[^>]*>([^<]*)<\/a>/i,
    /<a[^>]*background[^>]*href="([^"]*)"[^>]*>([^<]*)<\/a>/i,
  ];
  for (const pat of btnPatterns) {
    const m = html.match(pat);
    if (m) {
      sections.buttonUrl = m[1] === '#' ? '' : m[1];
      sections.buttonText = m[2].trim();
      break;
    }
  }

  // Extract footer — text after last <hr>
  const footerMatch = html.match(/<hr[^>]*>\s*<p[^>]*>([\s\S]*?)<\/p>\s*(?:<\/div>\s*)*<\/body>/i);
  if (footerMatch) {
    sections.footerText = footerMatch[1].replace(/<[^>]*>/g, '').trim();
  }

  // Extract body content — the main challenge
  // Strategy: find all <p>, <ul>, <ol>, <div> with text content between header and button/footer
  // Extract body content using regex (more reliable across different template formats)
  // Get all <p>, <ul>, <ol>, <table> tags from the HTML
  const pTags = html.match(/<p[^>]*>[\s\S]*?<\/p>/gi) || [];
  const ulTags = html.match(/<ul[^>]*>[\s\S]*?<\/ul>/gi) || [];
  const olTags = html.match(/<ol[^>]*>[\s\S]*?<\/ol>/gi) || [];
  
  // Filter to only body content (exclude footer, header text)
  const allTags = [...pTags, ...ulTags, ...olTags].filter(t => {
    const text = t.replace(/<[^>]*>/g, '').trim();
    if (!text || text.length < 5) return false;
    // Skip footer text
    if (text.includes('automated email') || text.includes('automated notification')) return false;
    // Skip company footer line  
    if (text.match(/^Tech Distribution Pty/i)) return false;
    // Skip if it matches the header title exactly
    if (text === sections.headerTitle) return false;
    return true;
  });
  
  if (allTags.length > 0) {
    // Clean dark-theme inline styles for readability in editor
    sections.bodyHtml = allTags.map(tag => 
      tag.replace(/color:\s*#[a-f0-9]{3,6};?/gi, '')
         .replace(/background:\s*#[a-f0-9]{3,6};?/gi, '')
         .replace(/border[^;]*#[a-f0-9]{3,6};?/gi, '')
         .replace(/style="[\s;]*"/gi, '')
    ).join('\n');
  }

  // Also try to extract table-based content (like customer details templates)
  if (!sections.bodyHtml) {
    const tableTags = html.match(/<table[^>]*>[\s\S]*?<\/table>/gi) || [];
    const contentTables = tableTags.filter(t => {
      const text = t.replace(/<[^>]*>/g, '').trim();
      return text.length > 20;
    });
    if (contentTables.length > 0) {
      sections.bodyHtml = contentTables.map(tag =>
        tag.replace(/color:\s*#[a-f0-9]{3,6};?/gi, '')
           .replace(/background:\s*#[a-f0-9]{3,6};?/gi, '')
      ).join('\n');
    }
  }

  return sections;
}

// Rich text toolbar component
function RichTextToolbar({ editorRef, variables, onInsertVariable }) {
  const exec = (cmd, val = null) => {
    editorRef.current?.focus();
    document.execCommand(cmd, false, val);
  };

  return (
    <div className="flex flex-wrap items-center gap-1 p-2 bg-zinc-800 border border-zinc-700 rounded-t-lg">
      <button type="button" onClick={() => exec('bold')} className="p-1.5 rounded hover:bg-zinc-700 text-zinc-300" title="Bold">
        <Bold className="h-4 w-4" />
      </button>
      <button type="button" onClick={() => exec('italic')} className="p-1.5 rounded hover:bg-zinc-700 text-zinc-300" title="Italic">
        <Italic className="h-4 w-4" />
      </button>
      <button type="button" onClick={() => exec('underline')} className="p-1.5 rounded hover:bg-zinc-700 text-zinc-300" title="Underline">
        <Underline className="h-4 w-4" />
      </button>
      <div className="w-px h-5 bg-zinc-700 mx-1" />
      <button type="button" onClick={() => {
        const url = prompt('Enter URL:');
        if (url) exec('createLink', url);
      }} className="p-1.5 rounded hover:bg-zinc-700 text-zinc-300" title="Insert Link">
        <Link className="h-4 w-4" />
      </button>
      <button type="button" onClick={() => exec('insertUnorderedList')} className="p-1.5 rounded hover:bg-zinc-700 text-zinc-300" title="Bullet List">
        <List className="h-4 w-4" />
      </button>
      <button type="button" onClick={() => exec('insertOrderedList')} className="p-1.5 rounded hover:bg-zinc-700 text-zinc-300" title="Numbered List">
        <ListOrdered className="h-4 w-4" />
      </button>
      <div className="w-px h-5 bg-zinc-700 mx-1" />
      <button type="button" onClick={() => exec('justifyLeft')} className="p-1.5 rounded hover:bg-zinc-700 text-zinc-300" title="Align Left">
        <AlignLeft className="h-4 w-4" />
      </button>
      <button type="button" onClick={() => exec('justifyCenter')} className="p-1.5 rounded hover:bg-zinc-700 text-zinc-300" title="Align Center">
        <AlignCenter className="h-4 w-4" />
      </button>
      <div className="w-px h-5 bg-zinc-700 mx-1" />
      <select
        onChange={(e) => { if (e.target.value) exec('fontSize', e.target.value); e.target.value = ''; }}
        className="bg-zinc-800 text-zinc-300 text-xs border border-zinc-700 rounded px-1 py-1"
        defaultValue=""
      >
        <option value="" disabled>Size</option>
        <option value="2">Small</option>
        <option value="3">Normal</option>
        <option value="4">Large</option>
        <option value="5">X-Large</option>
      </select>
      <div className="w-px h-5 bg-zinc-700 mx-1" />
      <button type="button" onClick={() => exec('undo')} className="p-1.5 rounded hover:bg-zinc-700 text-zinc-300" title="Undo">
        <Undo className="h-4 w-4" />
      </button>
      <button type="button" onClick={() => exec('redo')} className="p-1.5 rounded hover:bg-zinc-700 text-zinc-300" title="Redo">
        <Redo className="h-4 w-4" />
      </button>
      {variables && variables.length > 0 && (
        <>
          <div className="w-px h-5 bg-zinc-700 mx-1" />
          <div className="relative group">
            <button type="button" className="flex items-center gap-1 px-2 py-1 rounded bg-[#78BE20]/20 text-[#78BE20] text-xs hover:bg-[#78BE20]/30" title="Insert Variable">
              <Variable className="h-3.5 w-3.5" /> Variable
            </button>
            <div className="absolute top-full left-0 mt-1 bg-zinc-800 border border-zinc-700 rounded-lg shadow-xl z-50 hidden group-hover:block min-w-[180px]">
              {variables.map(v => (
                <button
                  key={v}
                  type="button"
                  onClick={() => onInsertVariable(v)}
                  className="block w-full text-left px-3 py-1.5 text-sm text-zinc-300 hover:bg-zinc-700 font-mono first:rounded-t-lg last:rounded-b-lg"
                >
                  {`{{${v}}}`}
                </button>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export default function AdminEmailTemplates() {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [previewDialogOpen, setPreviewDialogOpen] = useState(false);
  const [previewHtml, setPreviewHtml] = useState('');
  const [showRawHtml, setShowRawHtml] = useState(false);
  const [editForm, setEditForm] = useState({
    name: '', subject: '', description: '', is_active: true,
    html_content: '',
  });
  const [sections, setSections] = useState({
    headerTitle: '', bodyHtml: '', buttonText: '', buttonUrl: '', footerText: '', showLogo: true,
  });

  const bodyEditorRef = useRef(null);

  useEffect(() => { fetchTemplates(); }, []);

  const fetchTemplates = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/email-templates`);
      setTemplates(res.data);
    } catch (err) {
      console.error('Error fetching templates:', err);
    } finally {
      setLoading(false);
    }
  };

  const seedTemplates = async () => {
    try {
      const res = await axios.post(`${API}/admin/email-templates/seed`);
      toast.success(res.data.message);
      fetchTemplates();
    } catch (err) {
      toast.error('Failed to seed templates');
    }
  };

  const openEditDialog = (template) => {
    setSelectedTemplate(template);
    setEditForm({
      name: template.name,
      subject: template.subject,
      description: template.description || '',
      is_active: template.is_active !== false,
      html_content: template.html_content,
    });
    const parsed = parseTemplateSections(template.html_content);
    setSections(parsed);
    setShowRawHtml(false);
    setEditDialogOpen(true);
    // Populate body editor after dialog mounts
    setTimeout(() => {
      if (bodyEditorRef.current && parsed.bodyHtml) {
        bodyEditorRef.current.innerHTML = parsed.bodyHtml;
      }
    }, 300);
  };

  // Sync body editor content back to sections
  const syncBodyFromEditor = useCallback(() => {
    if (bodyEditorRef.current) {
      setSections(prev => ({ ...prev, bodyHtml: bodyEditorRef.current.innerHTML }));
    }
  }, []);

  // Build final HTML from sections
  const buildFinalHtml = useCallback(() => {
    const currentBody = bodyEditorRef.current ? bodyEditorRef.current.innerHTML : sections.bodyHtml;
    return buildEmailHtml({ ...sections, bodyHtml: currentBody });
  }, [sections]);

  const saveTemplate = async () => {
    try {
      const finalHtml = showRawHtml ? editForm.html_content : buildFinalHtml();
      await axios.put(`${API}/admin/email-templates/${selectedTemplate.slug}`, {
        ...editForm,
        html_content: finalHtml,
      });
      toast.success('Template saved');
      setEditDialogOpen(false);
      fetchTemplates();
    } catch (err) {
      toast.error('Failed to save template');
    }
  };

  const previewTemplate = (template) => {
    const html = editDialogOpen ? (showRawHtml ? editForm.html_content : buildFinalHtml()) : template.html_content;
    setPreviewHtml(html);
    setPreviewDialogOpen(true);
  };

  const insertVariable = (varName) => {
    const editor = bodyEditorRef.current;
    if (!editor) return;
    editor.focus();
    const text = `{{${varName}}}`;
    document.execCommand('insertHTML', false, `<span style="background:#78BE20;color:#000;padding:1px 4px;border-radius:3px;font-size:13px;">${text}</span>&nbsp;`);
  };

  // Set body editor content when dialog opens or sections change
  useEffect(() => {
    if (editDialogOpen && bodyEditorRef.current && !showRawHtml) {
      // Small delay to ensure the editor div is mounted
      const timer = setTimeout(() => {
        if (bodyEditorRef.current && sections.bodyHtml) {
          bodyEditorRef.current.innerHTML = sections.bodyHtml;
        }
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [editDialogOpen, showRawHtml, sections.bodyHtml]);

  return (
    <AdminLayout>
      <div data-testid="admin-email-templates">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-black text-white uppercase tracking-tight">Email Templates</h1>
            <p className="text-zinc-400 text-sm mt-1">Customize the emails sent to customers and staff</p>
          </div>
          <Button onClick={seedTemplates} variant="outline" className="border-zinc-700">
            <RefreshCw className="h-4 w-4 mr-2" /> Seed Default Templates
          </Button>
        </div>

        {/* Templates Grid */}
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="bg-[#18181b] border border-zinc-800 rounded-lg p-6 animate-pulse">
                <div className="h-6 bg-zinc-800 rounded w-1/2 mb-4" />
                <div className="h-4 bg-zinc-800 rounded w-3/4" />
              </div>
            ))}
          </div>
        ) : templates.length === 0 ? (
          <div className="bg-[#18181b] border border-zinc-800 rounded-lg p-12 text-center">
            <Mail className="h-12 w-12 text-zinc-600 mx-auto mb-4" />
            <h3 className="text-white text-lg font-medium mb-2">No Email Templates</h3>
            <p className="text-zinc-400 mb-4">Click "Seed Default Templates" to create the standard templates</p>
            <Button onClick={seedTemplates} className="bg-[#78BE20] text-black hover:bg-[#65a31b]">
              <RefreshCw className="h-4 w-4 mr-2" /> Seed Templates
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {templates.map(template => (
              <div key={template.slug} className="bg-[#18181b] border border-zinc-800 rounded-lg p-6 hover:border-zinc-700 transition-colors">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="bg-[#78BE20]/20 p-2 rounded-lg">
                      <Mail className="h-5 w-5 text-[#78BE20]" />
                    </div>
                    <div>
                      <h3 className="text-white font-medium">{template.name}</h3>
                      <p className="text-zinc-500 text-xs font-mono">{template.slug}</p>
                    </div>
                  </div>
                  <Badge className={template.is_active !== false ? 'bg-[#78BE20]/20 text-[#78BE20]' : 'bg-zinc-500/20 text-zinc-500'}>
                    {template.is_active !== false ? 'Active' : 'Inactive'}
                  </Badge>
                </div>
                <p className="text-zinc-400 text-sm mb-2">
                  <span className="text-zinc-500">Subject:</span> {template.subject}
                </p>
                {template.description && <p className="text-zinc-500 text-sm mb-4">{template.description}</p>}
                {template.variables?.length > 0 && (
                  <div className="mb-4">
                    <div className="flex flex-wrap gap-1">
                      {template.variables.map(v => (
                        <code key={v} className="text-xs bg-zinc-800 text-zinc-400 px-2 py-0.5 rounded">{`{{${v}}}`}</code>
                      ))}
                    </div>
                  </div>
                )}
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" className="flex-1 border-zinc-700" onClick={() => { setPreviewHtml(template.html_content); setPreviewDialogOpen(true); }}>
                    <Eye className="h-4 w-4 mr-1" /> Preview
                  </Button>
                  <Button size="sm" className="flex-1 bg-[#78BE20] text-black hover:bg-[#65a31b]" onClick={() => openEditDialog(template)}>
                    <Edit className="h-4 w-4 mr-1" /> Edit
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Edit Dialog — Visual Editor */}
        <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
          <DialogContent className="bg-zinc-900 border-zinc-800 max-w-5xl max-h-[92vh] overflow-y-auto">
            <DialogHeader>
              <div className="flex items-center justify-between">
                <DialogTitle className="text-white">Edit Email Template</DialogTitle>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setShowRawHtml(false)}
                    className={`px-3 py-1 rounded text-xs font-medium transition-colors ${!showRawHtml ? 'bg-[#78BE20] text-black' : 'bg-zinc-800 text-zinc-400 hover:text-white'}`}
                    data-testid="visual-editor-tab"
                  >
                    <Palette className="h-3 w-3 inline mr-1" /> Visual
                  </button>
                  <button
                    onClick={() => {
                      if (!showRawHtml) setEditForm(prev => ({ ...prev, html_content: buildFinalHtml() }));
                      setShowRawHtml(true);
                    }}
                    className={`px-3 py-1 rounded text-xs font-medium transition-colors ${showRawHtml ? 'bg-[#78BE20] text-black' : 'bg-zinc-800 text-zinc-400 hover:text-white'}`}
                    data-testid="html-editor-tab"
                  >
                    <Code className="h-3 w-3 inline mr-1" /> HTML
                  </button>
                </div>
              </div>
            </DialogHeader>

            {selectedTemplate && (
              <div className="space-y-4 mt-4">
                {/* Meta fields */}
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label className="text-xs text-zinc-500">Template Name</Label>
                    <Input value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} className="mt-1 bg-zinc-800 border-zinc-700 h-9" />
                  </div>
                  <div>
                    <Label className="text-xs text-zinc-500">Email Subject</Label>
                    <Input value={editForm.subject} onChange={(e) => setEditForm({ ...editForm, subject: e.target.value })} className="mt-1 bg-zinc-800 border-zinc-700 h-9" />
                  </div>
                  <div className="flex items-end gap-4">
                    <div className="flex-1">
                      <Label className="text-xs text-zinc-500">Slug</Label>
                      <Input value={selectedTemplate.slug} disabled className="mt-1 bg-zinc-800 border-zinc-700 opacity-50 h-9" />
                    </div>
                    <div className="flex items-center gap-2 pb-1">
                      <Switch checked={editForm.is_active} onCheckedChange={(c) => setEditForm({ ...editForm, is_active: c })} />
                      <Label className="text-xs">Active</Label>
                    </div>
                  </div>
                </div>

                {showRawHtml ? (
                  /* Raw HTML editor */
                  <div>
                    <Label className="text-xs text-zinc-500">HTML Content</Label>
                    <textarea
                      value={editForm.html_content}
                      onChange={(e) => setEditForm({ ...editForm, html_content: e.target.value })}
                      className="mt-1 w-full bg-zinc-800 border border-zinc-700 rounded-lg p-3 font-mono text-sm text-zinc-300 focus:outline-none focus:border-[#78BE20] resize-y"
                      rows={20}
                      data-testid="raw-html-editor"
                    />
                  </div>
                ) : (
                  /* Visual section editor */
                  <div className="grid grid-cols-2 gap-4">
                    {/* Left: Editor sections */}
                    <div className="space-y-4">
                      {/* Header */}
                      <div className="bg-zinc-800/50 border border-zinc-700 rounded-lg p-4">
                        <div className="flex items-center justify-between mb-2">
                          <Label className="text-xs text-zinc-400 uppercase tracking-wider">Header</Label>
                          <div className="flex items-center gap-2">
                            <Label className="text-xs text-zinc-500">Logo</Label>
                            <Switch checked={sections.showLogo} onCheckedChange={(c) => setSections({ ...sections, showLogo: c })} />
                          </div>
                        </div>
                        <Input
                          value={sections.headerTitle}
                          onChange={(e) => setSections({ ...sections, headerTitle: e.target.value })}
                          className="bg-zinc-900 border-zinc-600 text-lg font-bold"
                          placeholder="Email heading..."
                          data-testid="header-title-input"
                        />
                      </div>

                      {/* Body — Rich Text */}
                      <div className="bg-zinc-800/50 border border-zinc-700 rounded-lg overflow-hidden">
                        <div className="flex items-center justify-between px-4 pt-3 pb-1">
                          <Label className="text-xs text-zinc-400 uppercase tracking-wider">Body Content</Label>
                        </div>
                        <RichTextToolbar
                          editorRef={bodyEditorRef}
                          variables={selectedTemplate.variables}
                          onInsertVariable={insertVariable}
                        />
                        <div
                          ref={bodyEditorRef}
                          contentEditable
                          suppressContentEditableWarning
                          onInput={syncBodyFromEditor}
                          className="min-h-[200px] max-h-[350px] overflow-y-auto p-4 bg-zinc-900 text-zinc-200 text-sm focus:outline-none leading-relaxed"
                          style={{ wordBreak: 'break-word' }}
                          data-testid="body-editor"
                        />
                      </div>

                      {/* CTA Button */}
                      <div className="bg-zinc-800/50 border border-zinc-700 rounded-lg p-4">
                        <Label className="text-xs text-zinc-400 uppercase tracking-wider mb-2 block">Call-to-Action Button</Label>
                        <div className="grid grid-cols-2 gap-2">
                          <Input
                            value={sections.buttonText}
                            onChange={(e) => setSections({ ...sections, buttonText: e.target.value })}
                            className="bg-zinc-900 border-zinc-600"
                            placeholder="Button text (leave empty to hide)"
                            data-testid="button-text-input"
                          />
                          <Input
                            value={sections.buttonUrl}
                            onChange={(e) => setSections({ ...sections, buttonUrl: e.target.value })}
                            className="bg-zinc-900 border-zinc-600"
                            placeholder="{{admin_url}} or https://..."
                            data-testid="button-url-input"
                          />
                        </div>
                      </div>

                      {/* Footer */}
                      <div className="bg-zinc-800/50 border border-zinc-700 rounded-lg p-4">
                        <Label className="text-xs text-zinc-400 uppercase tracking-wider mb-2 block">Footer Text</Label>
                        <Input
                          value={sections.footerText}
                          onChange={(e) => setSections({ ...sections, footerText: e.target.value })}
                          className="bg-zinc-900 border-zinc-600 text-sm"
                          placeholder="Footer message..."
                          data-testid="footer-text-input"
                        />
                      </div>
                    </div>

                    {/* Right: Live Preview */}
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <Label className="text-xs text-zinc-400 uppercase tracking-wider">Live Preview</Label>
                        <span className="text-xs text-zinc-600">Updates as you type</span>
                      </div>
                      <div className="bg-[#f5f5f5] rounded-lg overflow-hidden border border-zinc-700" style={{ maxHeight: '600px', overflowY: 'auto' }}>
                        <LivePreview sections={sections} bodyEditorRef={bodyEditorRef} />
                      </div>
                    </div>
                  </div>
                )}

                {/* Action buttons */}
                <div className="flex gap-2 pt-4 border-t border-zinc-800">
                  <Button variant="outline" className="border-zinc-700" onClick={() => setEditDialogOpen(false)}>
                    <X className="h-4 w-4 mr-2" /> Cancel
                  </Button>
                  <Button variant="outline" className="border-zinc-700" onClick={() => previewTemplate(selectedTemplate)}>
                    <Eye className="h-4 w-4 mr-2" /> Full Preview
                  </Button>
                  <div className="flex-1" />
                  <Button className="bg-[#78BE20] text-black hover:bg-[#65a31b] px-8" onClick={saveTemplate} data-testid="save-template-btn">
                    <Save className="h-4 w-4 mr-2" /> Save Template
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Full Preview Dialog */}
        <Dialog open={previewDialogOpen} onOpenChange={setPreviewDialogOpen}>
          <DialogContent className="bg-zinc-900 border-zinc-800 max-w-3xl max-h-[90vh]">
            <DialogHeader>
              <DialogTitle className="text-white">Email Preview</DialogTitle>
            </DialogHeader>
            <div className="mt-4 bg-[#f5f5f5] rounded-lg overflow-hidden" style={{ maxHeight: '70vh', overflowY: 'auto' }}>
              <iframe srcDoc={previewHtml} title="Email Preview" className="w-full border-0" style={{ minHeight: '500px' }} />
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
}

// Live preview component — re-renders as sections change
function LivePreview({ sections, bodyEditorRef }) {
  const [bodyHtml, setBodyHtml] = useState(sections.bodyHtml);

  useEffect(() => {
    const interval = setInterval(() => {
      if (bodyEditorRef.current) {
        setBodyHtml(bodyEditorRef.current.innerHTML);
      }
    }, 500);
    return () => clearInterval(interval);
  }, [bodyEditorRef]);

  const html = buildEmailHtml({ ...sections, bodyHtml });

  return (
    <iframe
      srcDoc={html}
      title="Live Preview"
      className="w-full border-0"
      style={{ minHeight: '500px', pointerEvents: 'none' }}
    />
  );
}
